import { FC, FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { FormField, Input } from "@livechat/design-system-react-components";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { emoji } from "helpers/regexp";
import { useOrganization } from "hooks/use-organizations";
import { selectCompanyName } from "store/entities/license";
import { WelcomeTutorialViewActions, getIsUpdatingCompanyName } from "store/views/welcome-tutorial";

import { EmailAnimation } from "../components/EmailAnimation";
import StepScreen from "../components/StepScreen";
import { WelcomeTutorialStep } from "../helpers";

export const Mailbox: FC = () => {
  const dispatch = useDispatch();
  const isUpdating = useSelector(getIsUpdatingCompanyName);

  const companyName = useSelector(selectCompanyName);
  const { organization } = useOrganization();
  const { name: accountsCompanyName } = organization;

  const [company, setCompany] = useState(companyName || accountsCompanyName || "");
  const [zoomed, setZoomed] = useState(false);

  const handleCompanyChange = (e: FormEvent<HTMLInputElement>): void => {
    setCompany(e.currentTarget.value);
  };

  const onAnimationClicked = (): void => {
    trackEngagement("Welcome tutorial: Attempted click on Mailbox animation", EventPlace.WelcomeTutorial);
  };

  useEffect(() => {
    trackEngagement("Welcome tutorial: Mailbox section opened", EventPlace.WelcomeTutorial);
  }, []);

  const animationZoomIn = (): void => setZoomed(true);
  const animationZoomOut = (): void => setZoomed(false);

  const handleInput = (e: FormEvent<HTMLInputElement>): void => {
    e.currentTarget.value = e.currentTarget.value.replace(emoji, "");
  };

  const handleSubmit = (): void => {
    dispatch(WelcomeTutorialViewActions.setCompanyName({ companyName: company }));
  };

  const isValidName = company.trim().length > 0;

  return (
    <StepScreen
      step={WelcomeTutorialStep.Mailbox}
      title="Specify your name"
      description="Use your brand name, company name, or your own name to appear in recipients' messages."
      required
      allowNext={isValidName}
      onNext={handleSubmit}
      isNextLoading={isUpdating}
    >
      <FormField labelText="Enter your name">
        <Input
          id="company-name"
          placeholder="Cool Brand"
          inputSize="large"
          onChange={handleCompanyChange}
          onInput={handleInput}
          onFocus={animationZoomIn}
          onBlur={animationZoomOut}
          value={company}
          maxLength={30}
          disabled={isUpdating}
          autoFocus
          autoComplete="off"
          data-1p-ignore
          data-testid="company-name"
        />
      </FormField>
      <div onClick={onAnimationClicked}>
        <EmailAnimation name={company.trim()} zoomed={zoomed} />
      </div>
    </StepScreen>
  );
};
